import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ========================================
// 🔍 ASYNC THUNKS
// ========================================

export const fetchAgents = createAsyncThunk(
  'agents/fetchAgents',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // ✅ FIXED: Send sortBy and sortOrder separately to prevent SQL errors
      const params = {
        page: filters.page || 1,
        limit: filters.limit || 12,
        search: filters.search || '',
        specialization: filters.specialization || '',
        experience: filters.experience || '',
        city: filters.city || '',
        // ✅ FIXED: Separate sort parameters instead of combined 'name-asc' format
        sortBy: filters.sortBy || 'firstName',
        sortOrder: filters.sortOrder || 'ASC'
      };

      
      
      const response = await api.get('/agents', { params });
      
      
      return response.data;

    } catch (error) {
      console.error('❌ Agent fetch error:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch agents');
    }
  }
);

export const fetchAgentById = createAsyncThunk(
  'agents/fetchAgentById',
  async (id, { rejectWithValue }) => {
    try {
      
      
      const response = await api.get(`/agents/${id}`);
      
      
      return response.data;

    } catch (error) {
      console.error('❌ Fetch agent by ID error:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch agent');
    }
  }
);

export const fetchFeaturedAgents = createAsyncThunk(
  'agents/fetchFeaturedAgents',
  async (limit = 4, { rejectWithValue }) => {
    try {
      
      
      const response = await api.get(`/agents/featured?limit=${limit}`);
      
      
      return response.data;

    } catch (error) {
      console.error('❌ Fetch featured agents error:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured agents');
    }
  }
);

export const searchAgents = createAsyncThunk(
  'agents/searchAgents',
  async ({ query, filters = {} }, { rejectWithValue }) => {
    try {
      
      
      const params = {
        search: query,
        ...filters
      };

      const response = await api.get('/agents', { params });
      
      
      return response.data;

    } catch (error) {
      console.error('❌ Agent search error:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to search agents');
    }
  }
);

// ========================================
// 🏠 AGENT PROPERTIES
// ========================================

export const fetchAgentProperties = createAsyncThunk(
  'agents/fetchAgentProperties',
  async ({ agentId, params = {} }, { rejectWithValue }) => {
    try {
      
      
      const response = await api.get(`/agents/${agentId}/properties`, { params });
      
      
      return { agentId, properties: response.data };

    } catch (error) {
      console.error('❌ Fetch agent properties error:', error.response?.data);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch agent properties');
    }
  }
);

// ========================================
// 📊 INITIAL STATE
// ========================================

const initialState = {
  // Agent Lists
  agents: [],
  featuredAgents: [],
  searchResults: [],
  
  // Current Agent
  currentAgent: null,
  currentAgentProperties: [],
  
  // Loading States
  loading: false,
  featuredLoading: false,
  currentAgentLoading: false,
  searchLoading: false,
  propertiesLoading: false,
  
  // Error States
  error: null,
  featuredError: null,
  currentAgentError: null,
  searchError: null,
  propertiesError: null,
  
  // Pagination
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalAgents: 0,
    hasNext: false,
    hasPrev: false,
    limit: 12
  },
  
  // Search Pagination
  searchPagination: {
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
    hasNext: false,
    hasPrev: false,
    limit: 12
  },
  
  // Filters
  filters: {
    search: '',
    specialization: '',
    experience: '',
    city: '',
    sortBy: 'firstName',
    sortOrder: 'ASC',
    page: 1
  },
  
  // Stats
  totalActiveAgents: 0,
  totalFeaturedAgents: 0,
  averageRating: 0,
  
  // Cache
  lastFetch: null,
  cacheExpiry: 5 * 60 * 1000 // 5 minutes
};

// ========================================
// 🧬 AGENT SLICE
// ========================================

const agentSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    // ✅ Clear Errors
    clearError: (state) => {
      state.error = null;
      state.featuredError = null;
      state.currentAgentError = null;
      state.searchError = null;
      state.propertiesError = null;
    },
    
    // ✅ Clear Current Agent
    clearCurrentAgent: (state) => {
      state.currentAgent = null;
      state.currentAgentProperties = [];
      state.currentAgentError = null;
    },
    
    // ✅ Set Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // ✅ Clear Filters
    clearFilters: (state) => {
      state.filters = { ...initialState.filters };
    },
    
    // ✅ Clear Search Results
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
      state.searchPagination = { ...initialState.searchPagination };
    },
    
    // ✅ Update Agent Locally (for optimistic updates)
    updateAgentLocally: (state, action) => {
      const { id, updates } = action.payload;
      
      // Update in main agents list
      const agentIndex = state.agents.findIndex(agent => agent.id === id);
      if (agentIndex !== -1) {
        state.agents[agentIndex] = { ...state.agents[agentIndex], ...updates };
      }
      
      // Update in featured agents if exists
      const featuredIndex = state.featuredAgents.findIndex(agent => agent.id === id);
      if (featuredIndex !== -1) {
        state.featuredAgents[featuredIndex] = { ...state.featuredAgents[featuredIndex], ...updates };
      }
      
      // Update current agent if it's the same
      if (state.currentAgent && state.currentAgent.id === id) {
        state.currentAgent = { ...state.currentAgent, ...updates };
      }
    },
    
    // ✅ Set Cache Timestamp
    setCacheTimestamp: (state) => {
      state.lastFetch = Date.now();
    }
  },
  
  extraReducers: (builder) => {
    builder
      // ========================================
      // 👥 FETCH ALL AGENTS
      // ========================================
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        
        // ✅ Handle different response formats
        if (action.payload.data && action.payload.data.agents) {
          state.agents = action.payload.data.agents;
          state.pagination = action.payload.data.pagination;
          state.totalActiveAgents = action.payload.data.pagination.totalAgents;
        } else if (action.payload.agents) {
          state.agents = action.payload.agents;
          state.pagination = action.payload.pagination || state.pagination;
        } else if (Array.isArray(action.payload)) {
          state.agents = action.payload;
        }
        
        state.lastFetch = Date.now();
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.agents = [];
      })
      
      // ========================================
      // 👤 FETCH AGENT BY ID
      // ========================================
      .addCase(fetchAgentById.pending, (state) => {
        state.currentAgentLoading = true;
        state.currentAgentError = null;
      })
      .addCase(fetchAgentById.fulfilled, (state, action) => {
        state.currentAgentLoading = false;
        
        // ✅ Handle different response formats
        if (action.payload.data && action.payload.data.agent) {
          state.currentAgent = action.payload.data.agent;
        } else if (action.payload.agent) {
          state.currentAgent = action.payload.agent;
        } else {
          state.currentAgent = action.payload;
        }
      })
      .addCase(fetchAgentById.rejected, (state, action) => {
        state.currentAgentLoading = false;
        state.currentAgentError = action.payload;
        state.currentAgent = null;
      })
      
      // ========================================
      // ⭐ FETCH FEATURED AGENTS
      // ========================================
      .addCase(fetchFeaturedAgents.pending, (state) => {
        state.featuredLoading = true;
        state.featuredError = null;
      })
      .addCase(fetchFeaturedAgents.fulfilled, (state, action) => {
        state.featuredLoading = false;
        
        // ✅ Handle different response formats
        if (action.payload.data && action.payload.data.agents) {
          state.featuredAgents = action.payload.data.agents;
        } else if (action.payload.agents) {
          state.featuredAgents = action.payload.agents;
        } else if (Array.isArray(action.payload)) {
          state.featuredAgents = action.payload;
        }
        
        state.totalFeaturedAgents = state.featuredAgents.length;
        
        // Calculate average rating
        if (state.featuredAgents.length > 0) {
          const totalRating = state.featuredAgents.reduce((sum, agent) => sum + (agent.rating || 0), 0);
          state.averageRating = (totalRating / state.featuredAgents.length).toFixed(1);
        }
      })
      .addCase(fetchFeaturedAgents.rejected, (state, action) => {
        state.featuredLoading = false;
        state.featuredError = action.payload;
        state.featuredAgents = [];
      })
      
      // ========================================
      // 🔍 SEARCH AGENTS
      // ========================================
      .addCase(searchAgents.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchAgents.fulfilled, (state, action) => {
        state.searchLoading = false;
        
        // ✅ Handle different response formats
        if (action.payload.data && action.payload.data.agents) {
          state.searchResults = action.payload.data.agents;
          state.searchPagination = action.payload.data.pagination;
        } else if (action.payload.agents) {
          state.searchResults = action.payload.agents;
          state.searchPagination = action.payload.pagination || state.searchPagination;
        } else if (Array.isArray(action.payload)) {
          state.searchResults = action.payload;
        }
      })
      .addCase(searchAgents.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
        state.searchResults = [];
      })
      
      // ========================================
      // 🏠 FETCH AGENT PROPERTIES
      // ========================================
      .addCase(fetchAgentProperties.pending, (state) => {
        state.propertiesLoading = true;
        state.propertiesError = null;
      })
      .addCase(fetchAgentProperties.fulfilled, (state, action) => {
        state.propertiesLoading = false;
        
        // ✅ Store properties for the specific agent
        if (action.payload.properties) {
          state.currentAgentProperties = action.payload.properties.data || action.payload.properties;
        }
      })
      .addCase(fetchAgentProperties.rejected, (state, action) => {
        state.propertiesLoading = false;
        state.propertiesError = action.payload;
        state.currentAgentProperties = [];
      });
  }
});

// ========================================
// 🎯 ACTIONS EXPORT
// ========================================

export const {
  clearError,
  clearCurrentAgent,
  setFilters,
  clearFilters,
  clearSearchResults,
  updateAgentLocally,
  setCacheTimestamp
} = agentSlice.actions;

// ========================================
// 🎯 SELECTORS EXPORT
// ========================================

// Basic Selectors
export const selectAgents = (state) => state.agents.agents;
export const selectFeaturedAgents = (state) => state.agents.featuredAgents;
export const selectCurrentAgent = (state) => state.agents.currentAgent;
export const selectCurrentAgentProperties = (state) => state.agents.currentAgentProperties;
export const selectSearchResults = (state) => state.agents.searchResults;

// Loading Selectors
export const selectAgentLoading = (state) => state.agents.loading;
export const selectFeaturedAgentsLoading = (state) => state.agents.featuredLoading;
export const selectCurrentAgentLoading = (state) => state.agents.currentAgentLoading;
export const selectSearchLoading = (state) => state.agents.searchLoading;
export const selectPropertiesLoading = (state) => state.agents.propertiesLoading;

// Error Selectors
export const selectAgentError = (state) => state.agents.error;
export const selectFeaturedAgentsError = (state) => state.agents.featuredError;
export const selectCurrentAgentError = (state) => state.agents.currentAgentError;
export const selectSearchError = (state) => state.agents.searchError;
export const selectPropertiesError = (state) => state.agents.propertiesError;

// Pagination Selectors
export const selectAgentPagination = (state) => state.agents.pagination;
export const selectSearchPagination = (state) => state.agents.searchPagination;

// Filter Selectors
export const selectAgentFilters = (state) => state.agents.filters;

// Stats Selectors
export const selectTotalActiveAgents = (state) => state.agents.totalActiveAgents;
export const selectTotalFeaturedAgents = (state) => state.agents.totalFeaturedAgents;
export const selectAverageRating = (state) => state.agents.averageRating;

// Cache Selectors
export const selectLastFetch = (state) => state.agents.lastFetch;
export const selectCacheExpiry = (state) => state.agents.cacheExpiry;

// ✅ Computed Selectors
export const selectAgentById = (state, agentId) => 
  state.agents.agents.find(agent => agent.id === parseInt(agentId));

export const selectAgentsBySpecialization = (state, specialization) =>
  state.agents.agents.filter(agent => 
    agent.specialization && agent.specialization.toLowerCase().includes(specialization.toLowerCase())
  );

export const selectTopRatedAgents = (state, limit = 5) =>
  [...state.agents.agents]
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit);

export const selectAgentsStats = (state) => ({
  total: state.agents.totalActiveAgents,
  featured: state.agents.totalFeaturedAgents,
  averageRating: state.agents.averageRating,
  withProperties: state.agents.agents.filter(agent => agent.totalSales > 0).length,
  newThisMonth: state.agents.agents.filter(agent => {
    if (!agent.createdAt) return false;
    const agentDate = new Date(agent.createdAt);
    const now = new Date();
    return agentDate.getMonth() === now.getMonth() && agentDate.getFullYear() === now.getFullYear();
  }).length
});

export const selectIsCacheValid = (state) => {
  if (!state.agents.lastFetch) return false;
  return (Date.now() - state.agents.lastFetch) < state.agents.cacheExpiry;
};

export default agentSlice.reducer;
